import type { CuBreakdown } from '@sol-tx-visualizer/tx-decode';

interface Props {
  breakdown: CuBreakdown;
}

export default function CuTimeline({ breakdown }: Props) {
  const maxCu = Math.max(
    breakdown.limit,
    ...breakdown.perInstruction.map((i) => i.cu),
    1
  );

  return (
    <div className="p-4 space-y-4">
      {/* Summary */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex gap-4">
          <span className="text-slate-400">
            Consumed:{' '}
            <span className="font-mono text-slate-200">
              {breakdown.totalConsumed.toLocaleString()}
            </span>
          </span>
          <span className="text-slate-400">
            Limit:{' '}
            <span className="font-mono text-slate-200">
              {breakdown.limit.toLocaleString()}
            </span>
          </span>
          <span className="text-slate-400">
            Utilization:{' '}
            <span
              className={`font-mono ${
                breakdown.totalConsumed / breakdown.limit > 0.9
                  ? 'text-rose-400'
                  : 'text-emerald-400'
              }`}
            >
              {((breakdown.totalConsumed / breakdown.limit) * 100).toFixed(1)}%
            </span>
          </span>
        </div>
      </div>

      {/* Overall bar */}
      <div className="space-y-1">
        <div className="h-2 rounded-full bg-slate-800 overflow-hidden flex">
          <div
            className="h-full bg-violet-500 transition-all"
            style={{ width: `${(breakdown.totalConsumed / maxCu) * 100}%` }}
          />
          {breakdown.limit > breakdown.totalConsumed && (
            <div
              className="h-full bg-slate-700 transition-all"
              style={{
                width: `${((breakdown.limit - breakdown.totalConsumed) / maxCu) * 100}%`,
              }}
            />
          )}
        </div>
        <div className="flex justify-between text-[10px] text-slate-600 font-mono">
          <span>0</span>
          <span>{maxCu.toLocaleString()} CU</span>
        </div>
      </div>

      {/* Per-instruction bars */}
      <div className="space-y-2 max-h-[480px] overflow-auto">
        {breakdown.perInstruction.length === 0 && (
          <div className="text-sm text-slate-500 py-8 text-center">
            No CU data available.
          </div>
        )}
        {breakdown.perInstruction.map((item) => (
          <div key={item.id} className="group">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-300">
                #{item.index} {item.programName}
              </span>
              <span className="font-mono text-slate-500">
                {item.cu.toLocaleString()} CU ({item.percentOfTotal.toFixed(1)}%)
              </span>
            </div>
            <div className="h-6 rounded-md bg-slate-800 overflow-hidden relative">
              <div
                className="h-full rounded-md bg-gradient-to-r from-cyan-600 to-cyan-400 transition-all flex items-center px-2"
                style={{ width: `${(item.cu / maxCu) * 100}%` }}
              >
                {item.cu / maxCu > 0.15 && (
                  <span className="text-[10px] font-medium text-slate-950 truncate">
                    {item.percentOfTotal.toFixed(0)}%
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recommendation */}
      {breakdown.overProvisioned && (
        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-400">
          <span className="font-semibold">Suggestion:</span> Lower CU limit to{' '}
          <span className="font-mono">{breakdown.recommendedLimit.toLocaleString()}</span> for
          estimated{' '}
          <span className="font-mono">{breakdown.estimatedSavingsPercent.toFixed(1)}%</span>{' '}
          priority-fee reduction.
        </div>
      )}
    </div>
  );
}
